import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, ArrowRight } from "lucide-react";
import logoAsset from "@/assets/logo-selectus-branco.webp.asset.json";
import bgAsset from "@/assets/bg-obrigado.webp.asset.json";

export const Route = createFileRoute("/obrigado")({
  head: () => ({
    meta: [
      { title: "Obrigado! | Selectus RH" },
      { name: "description", content: "Sua solicitação foi enviada com sucesso. Entraremos em contato em até 48h." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Obrigado! | Selectus RH" },
      { property: "og:description", content: "Sua solicitação foi enviada com sucesso. Entraremos em contato em até 48h." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
    ],
    links: [{ rel: "canonical", href: "https://lp.selectusrh.com/obrigado" }],
  }),
  component: ObrigadoPage,
});

const WHATSAPP_LINK =
  "https://wa.me/5511919359286?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Gostaria%20de%20um%20or%C3%A7amento%20para%20contrata%C3%A7%C3%A3o%20de%20M%C3%A3o%20de%20Obra%20Tempor%C3%A1ria%20ou%20Terceirizada%20para%20Constru%C3%A7%C3%A3o%20Civil.";

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.585 5.944L.577 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function ObrigadoPage() {
  return (
    <main
      className="relative flex min-h-screen flex-col items-center justify-center px-6 text-center"
      style={{ backgroundImage: `url(${bgAsset.url})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundColor: "oklch(0.19 0.11 270 / 0.7)" }}
      />
      <div className="relative z-10 mx-auto max-w-lg">
        <a href="/" aria-label="Selectus RH - Página inicial" className="mb-10 inline-block">
          <img
            src={logoAsset.url}
            alt="Selectus RH - Mão de obra temporária para construção civil"
            width="180"
            height="45"
            className="h-11 w-auto"
          />
        </a>

        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/10 mx-auto mb-6">
          <Check className="h-10 w-10 text-brand-orange" strokeWidth={2.5} />
        </div>

        <h1 className="font-display text-4xl font-bold tracking-tight text-white md:text-5xl">
          OBRIGADO!
        </h1>

        <p className="mt-6 text-base leading-relaxed text-white/80 md:text-lg">
          Sua solicitação foi enviada com sucesso e entraremos em contato em até 48h.
        </p>

        <p className="mt-4 text-sm leading-relaxed text-white/60">
          Caso tenha urgência, clique no botão abaixo e fale agora com nosso consultor de novos negócios.
        </p>

        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 font-display text-[13px] font-semibold tracking-wide text-white transition hover:bg-[#128C7E]"
        >
          <WhatsAppIcon className="h-5 w-5" />
          Fale agora com nosso consultor
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </a>

        <div className="mt-10">
          <Link
            to="/"
            className="text-sm text-white/50 underline underline-offset-4 transition hover:text-white"
          >
            Voltar para o início
          </Link>
        </div>
      </div>
    </main>
  );
}
